import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import GenericModal from './GenericModal';
import QuickEditObjectiveModal from './QuickEditObjectiveModal';
import { Sparkles, Lock, Unlock } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import objetivosApi from '../../api/objetivos';
import usersApi, { setupSteps }  from '../../api/user';
import { formatCurrencyAmount } from '../../utils/currency';
import optimizationsApi from '../../api/optimizations';
import { useOptimizationGenerationToast } from '../context/OptimizationGenerationToastContext';
import { normalizeGroupsResponse } from '../../utils/optimizationTransformers';

const CreateObjectivesModal = ({ isOpen, onClose, onSuccess, platform = null, selectedConversions = [], selectedObjectiveMetrics = [], autoGenerateOnOpen = false }) => {
  const { auth } = useContext(AuthContext);
  const { runGeneration } = useOptimizationGenerationToast();
  const [loading, setLoading] = useState(false);
  const [objectivesGenerated, setObjectivesGenerated] = useState(false);
  const [objectives, setObjectives] = useState([]);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [isContinuing, setIsContinuing] = useState(false);
  const [quickEditObjectiveId, setQuickEditObjectiveId] = useState(null);
  const hasAutoGeneratedRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setObjectivesGenerated(false);
      setObjectives([]);
      setLoading(false);
      setIsUnlocking(false);
      setIsContinuing(false);
      hasAutoGeneratedRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setQuickEditObjectiveId(null);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && autoGenerateOnOpen && !hasAutoGeneratedRef.current) {
      hasAutoGeneratedRef.current = true;
      handleGenerate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, autoGenerateOnOpen]);

  const handleGenerate = async () => {
    setIsUnlocking(true);
    setLoading(true);
    
    // Efecto estimulante: esperar un momento antes de empezar
    await new Promise(resolve => setTimeout(resolve, 500));
    
    try {
      // Determinar qué plataformas están conectadas
      const hasGoogleConnected = Boolean(auth?.user?.google_ads_customer_id);
      const hasMetaConnected = Boolean(auth?.user?.meta_ad_account_id);
      
      // Si viene el prop platform, usarlo (para paso 4 - objetivos por plataforma)
      let platformsToUse = [];
      if (platform) {
        platformsToUse = [platform.toLowerCase()];
      } else {
        // Determinar plataformas a usar según conexiones
        if (hasGoogleConnected) {
          platformsToUse.push('google');
        }
        if (hasMetaConnected) {
          platformsToUse.push('meta');
        }
      }
      
      if (platformsToUse.length === 0) {
        toast.error('Necesitas conectar al menos una cuenta publicitaria');
        setIsUnlocking(false);
        setLoading(false);
        return;
      }
      
      // Llamar a los endpoints correspondientes en paralelo
      const promises = [];
      
      const conversionIds = Array.isArray(selectedConversions) ? selectedConversions : [];
      const objectiveMetrics = Array.isArray(selectedObjectiveMetrics)
        ? selectedObjectiveMetrics
        : [];

      if (platformsToUse.includes('google')) {
        promises.push(objetivosApi.autoGenerateGoogle(conversionIds, objectiveMetrics));
      }

      if (platformsToUse.includes('meta')) {
        promises.push(objetivosApi.autoGenerateMeta(conversionIds, objectiveMetrics));
      }
      
      // Esperar todas las respuestas
      const responses = await Promise.all(promises);
      
      // Combinar todos los objetivos generados
      let allObjectives = [];
      
      responses.forEach((response) => {
        const objetivos = (response.data && response.data.objetivos) || [];
        allObjectives.push(...objetivos);
      });
      
      if (allObjectives.length === 0) {
        toast.error('Tu cuenta todavía no tiene datos suficientes para poder crearte tus objetivos 😱\nApenas tengamos más información, los objetivos se crearán automáticamente 👌');
        setIsUnlocking(false);
        setLoading(false);
        return;
      }
      
      // Actualizar setup_step asíncronamente (no bloquea el flujo principal)
      (async () => {
        try {
          let completedSteps = [setupSteps.CREA_OBJETIVOS_AUTOMATICOS_1];
          if (hasGoogleConnected && hasMetaConnected) {
            completedSteps.push(setupSteps.CREA_OBJETIVOS_AUTOMATICOS_2);
          }
          
          await usersApi.setSetupSteps(completedSteps);
        } catch (error) {
          console.error('Error actualizando setup_step:', error);
          // No mostrar error al usuario ya que es asíncrono y no crítico
        }
      })();
      
      setObjectives(allObjectives);
      setObjectivesGenerated(true);
      setIsUnlocking(false);
      toast.success('¡Objetivos creados con éxito! 🎯');
    } catch (error) {
      console.error('Error generando objetivos:', error);
      toast.error(error.response?.data?.error || 'Error al generar objetivos automáticos');
      setIsUnlocking(false);
    } finally {
      setLoading(false);
    }
  };

  const getMetricEmoji = (metrica) => {
    const m = (metrica || '').toUpperCase();
    if (m === 'ROAS') return '💰';
    if (m === 'CPA') return '⚖️';
    if (m === 'PRESUPUESTO' || m === 'INVERSIÓN') return '💸';
    if (m === 'CONVERSIONES') return '🛒';
    return '🎯';
  };

  const formatTarget = (target, metrica, currency = null) => {
    if (!target && target !== 0) return 'N/A';
    
    try {
      const numTarget = parseFloat(target);
      if (isNaN(numTarget)) return 'N/A';
      
      switch (metrica) {
        case 'ROAS':
          return `${numTarget.toFixed(2)}x`;
        case 'CPA':
        case 'Presupuesto':
          return formatCurrencyAmount(numTarget, currency, {
            locale: 'es-ES',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        case 'Conversiones':
          return `${Math.floor(numTarget).toLocaleString('es-ES')}`;
        default:
          return target.toString();
      }
    } catch (error) {
      console.error('Error formateando target:', error);
      return 'N/A';
    }
  };

  const formatFecha = (fechaStr) => {
    if (!fechaStr) return 'N/A';
    
    // Parsear fechas manualmente para evitar problemas de zona horaria
    if (typeof fechaStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fechaStr)) {
      const [year, month, day] = fechaStr.split('-').map(Number);
      const fecha = new Date(year, month - 1, day);
      return fecha.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    return new Date(fechaStr).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handleContinuar = async () => {
    if (isContinuing) return;
    const brandId = auth?.user?.active_brand_id;
    if (!brandId) {
      toast.error('No hay marca activa seleccionada para generar optimizaciones');
      return;
    }
    setIsContinuing(true);
    try {
      onClose();
      try {
        if (onSuccess) await onSuccess();
      } catch (e) {
        console.error('Error en onSuccess al cerrar modal de objetivos:', e);
      }
      const hasGoogleConnected = Boolean(auth?.user?.google_ads_customer_id);
      const hasMetaConnected = Boolean(auth?.user?.meta_ad_account_id);
      let platformsArg;
      if (platform) {
        const p = String(platform).toLowerCase();
        platformsArg = p === 'google' || p === 'meta' ? [p] : undefined;
      } else {
        platformsArg = [];
        if (hasGoogleConnected) platformsArg.push('google');
        if (hasMetaConnected) platformsArg.push('meta');
      }
      const result = await runGeneration({
        brandId,
        ...(platformsArg?.length ? { platforms: platformsArg } : {}),
        loadCount: async () => {
          const response = await optimizationsApi.getActiveOptimizations();
          const { groups } = normalizeGroupsResponse(response?.data);
          return groups.length;
        },
      });
      if (!result.ok) return;
      if (result.count === 0) {
        toast('No encontramos optimizaciones nuevas por ahora');
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('objectives-created'));
      }
    } catch (error) {
      console.error('Error ejecutando motor de optimizaciones:', error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'No se pudieron generar las optimizaciones';
      toast.error(errorMessage);
    } finally {
      setIsContinuing(false);
    }
  };

  const handleEditarObjetivo = (objetivo) => {
    if (!objetivo?.id) return;
    setQuickEditObjectiveId(objetivo.id);
  };

  const handleQuickEditSaved = useCallback((updated) => {
    if (!updated?.id) return;
    setObjectives((prev) =>
      prev.map((o) => (o.id === updated.id ? { ...o, ...updated } : o))
    );
  }, []);

  return (
    <>
    <QuickEditObjectiveModal
      isOpen={quickEditObjectiveId != null}
      objectiveId={quickEditObjectiveId}
      onClose={() => setQuickEditObjectiveId(null)}
      onSaved={handleQuickEditSaved}
      overlayZIndex={10100}
    />
    <GenericModal
      isOpen={isOpen}
      toggle={onClose}
      titleSlot={
        !objectivesGenerated ? (
          <h2 className="text-2xl md:text-3xl font-bold text-center w-full">
            <span className="bg-gradient-to-r from-purple-400 via-blue-400 to-purple-400 bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
              Desbloqueá tus objetivos generados con IA
            </span>
          </h2>
        ) : (
          <div className="flex flex-col items-center w-full">
            <h2 className="text-2xl md:text-3xl font-bold text-center w-full">
              <span className="bg-gradient-to-r from-purple-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                ¡Listo! Objetivos creados con éxito.
              </span>
            </h2>
          </div>
        )
      }
      description={
        objectivesGenerated
          ? 'El siguiente paso es buscar optimizaciones para tus anuncios. Podés editar los objetivos abajo o continuar con el siguiente paso.'
          : ''
      }
      onClose={onClose}
      showSubmitButton={false}
      closable={true}
      showLogo={false}
      width={objectivesGenerated ? '640px' : '600px'}
      contentPadding="2.5rem 2rem"
    >
      <div className="space-y-6">
        {!objectivesGenerated ? (
          <>
            <div className="text-center py-8">
              {loading ? (
                <div className="space-y-4">
                  <div className="relative w-24 h-24 mx-auto">
                    <div className="absolute inset-0 border-4 border-purple-500/30 rounded-full"></div>
                    <div className="absolute inset-0 border-4 border-transparent border-t-purple-500 rounded-full animate-spin"></div>
                    <Sparkles className="absolute inset-0 m-auto w-8 h-8 text-purple-400 animate-pulse" />
                  </div>
                  <div className="space-y-2">
                    <p className="text-white/90 font-medium">Generando objetivos con IA...</p>
                    <p className="text-white/60 text-sm">
                      Analizando tu cuenta y creando objetivos personalizados.
                    </p>
                    <p className="text-white/60 text-sm">
                      Esto puede tomar unos minutos. No cierres esta página.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-8 py-4">
                  <button
                    type="button"
                    onClick={handleGenerate}
                    disabled={loading}
                    className={`w-full px-8 py-4 bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600 text-white rounded-2xl font-bold text-lg hover:from-purple-700 hover:via-blue-700 hover:to-purple-700 transition-all duration-300 shadow-2xl hover:shadow-purple-500/50 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden group ${
                      isUnlocking ? 'scale-105 shadow-2xl shadow-purple-500/50' : 'hover:scale-[1.03] active:scale-[0.97]'
                    }`}
                  >
                    {isUnlocking && (
                      <>
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-400/50 via-blue-400/50 to-purple-400/50 animate-pulse"></div>
                      </>
                    )}
                    <div className="flex items-center justify-center gap-3 relative z-10">
                      {isUnlocking ? (
                        <>
                          <Unlock className="w-6 h-6 animate-bounce" />
                          <span className="text-xl">Desbloqueando...</span>
                        </>
                      ) : (
                        <>
                          <Lock className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
                          <span className="text-xl">Desbloquear</span>
                        </>
                      )}
                    </div>
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-2">
              {objectives.map((objetivo) => {
                const metrica = objetivo.metrica_relacionada || '';
                const emoji = getMetricEmoji(metrica);
                const targetStr = formatTarget(objetivo.target, metrica, objetivo.currency);
                const fechaInicio = formatFecha(objetivo.fecha_inicio);
                const fechaFin = formatFecha(objetivo.fecha_fin);

                return (
                  <div
                    key={objetivo.id}
                    className="backdrop-blur-md bg-white/5 border border-white/10 rounded-lg p-2.5 hover:bg-white/10 hover:border-white/20 transition-all duration-300"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0 flex items-center gap-2">
                        <span className="text-lg shrink-0">{emoji}</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-semibold text-white leading-tight">
                            {metrica}: {targetStr} · {fechaInicio} – {fechaFin}
                          </p>
                          <p className="text-xs font-normal text-white/70 truncate leading-tight">{objetivo.nombre}</p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleEditarObjetivo(objetivo)}
                        className="shrink-0 min-h-[44px] min-w-[44px] md:min-h-8 md:min-w-0 px-3 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-xs font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                      >
                        Editar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-white/75 text-sm text-center">
              Al continuar vamos a buscar optimizaciones para tus anuncios. Se cierra este modal y te mostramos el avance.
            </p>

            <div className="pt-4">
              <button
                type="button"
                onClick={handleContinuar}
                disabled={isContinuing}
                className="w-full min-h-[48px] px-6 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-bold text-base hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isContinuing ? 'Iniciando…' : 'Buscar optimizaciones para mis anuncios'}
              </button>
            </div>
          </div>
        )}
      </div>
    </GenericModal>
    </>
  );
};

export default CreateObjectivesModal;

